// ── Módulos por cuenta (panel de plataforma, 2026-08-17) ─────────────────────
// Fuente ÚNICA del mapeo módulo → rutas de API, rutas de nav, jobs del worker
// y dependencias. Server, worker y web leen de acá. Sin esto el mapeo se
// duplica en cuatro lugares y se desincroniza en el primer módulo nuevo — y
// una desincronización acá no se ve: el menú esconde el módulo pero la ruta
// sigue abierta, que es exactamente el interruptor decorativo que este trabajo
// existe para evitar.
export const ACCOUNT_MODULE_KEYS = [
    'email', 'leads', 'funnels', 'contacts', 'forms', 'projects', 'reports', 'tasks', 'billing', 'messaging',
];
export const ACCOUNT_MODULE_KEY_SET = new Set(ACCOUNT_MODULE_KEYS);
export function isAccountModuleKey(value) {
    return ACCOUNT_MODULE_KEY_SET.has(value);
}
export function allModulesEnabled() {
    return Object.fromEntries(ACCOUNT_MODULE_KEYS.map(k => [k, true]));
}
export const ACCOUNT_MODULES = [
    {
        key: 'contacts',
        labelKey: 'platformAdmin.modules.contacts.label',
        descriptionKey: 'platformAdmin.modules.contacts.description',
        dependsOn: [],
        apiPrefixes: ['/api/contacts', '/api/contact-tags', '/api/contact-categories', '/api/relationship-categories'],
        publicPrefixes: [],
        navPaths: ['/contacts'],
    },
    {
        key: 'email',
        labelKey: 'platformAdmin.modules.email.label',
        descriptionKey: 'platformAdmin.modules.email.description',
        dependsOn: [],
        apiPrefixes: ['/api/email', '/api/email-categories'],
        publicPrefixes: [],
        navPaths: ['/email'],
    },
    {
        key: 'leads',
        labelKey: 'platformAdmin.modules.leads.label',
        descriptionKey: 'platformAdmin.modules.leads.description',
        dependsOn: ['contacts'],
        apiPrefixes: ['/api/leads', '/api/lead-pipelines', '/api/rules', '/api/outreach', '/api/outreach-replies', '/api/scraping-telemetry'],
        publicPrefixes: ['/api/leads/web-form'],
        navPaths: ['/leads'],
    },
    {
        key: 'funnels',
        labelKey: 'platformAdmin.modules.funnels.label',
        descriptionKey: 'platformAdmin.modules.funnels.description',
        // Ninguna columna de `opportunities` que apunte a otro módulo es NOT NULL
        // (contact_id, lead_id, submission_id, estimate_id, project_id son las
        // cinco nullable — db/schema.ts): un embudo funciona con cartas sueltas y
        // por eso no depende de nada.
        dependsOn: [],
        apiPrefixes: ['/api/funnels', '/api/opportunities'],
        publicPrefixes: [],
        navPaths: ['/embudos'],
    },
    {
        key: 'forms',
        labelKey: 'platformAdmin.modules.forms.label',
        descriptionKey: 'platformAdmin.modules.forms.description',
        dependsOn: ['contacts'],
        apiPrefixes: ['/api/forms'],
        publicPrefixes: ['/f'],
        navPaths: ['/formularios'],
    },
    {
        key: 'projects',
        labelKey: 'platformAdmin.modules.projects.label',
        descriptionKey: 'platformAdmin.modules.projects.description',
        dependsOn: ['contacts'],
        apiPrefixes: ['/api/projects'],
        publicPrefixes: ['/c', '/p', '/content-plan'],
        navPaths: ['/proyectos'],
    },
    {
        key: 'reports',
        labelKey: 'platformAdmin.modules.reports.label',
        descriptionKey: 'platformAdmin.modules.reports.description',
        dependsOn: [],
        apiPrefixes: ['/api/reports'],
        publicPrefixes: ['/r'],
        navPaths: ['/reports'],
    },
    {
        key: 'tasks',
        labelKey: 'platformAdmin.modules.tasks.label',
        descriptionKey: 'platformAdmin.modules.tasks.description',
        dependsOn: [],
        apiPrefixes: ['/api/tasks'],
        publicPrefixes: [],
        navPaths: ['/tasks'],
    },
    {
        key: 'billing',
        labelKey: 'platformAdmin.modules.billing.label',
        descriptionKey: 'platformAdmin.modules.billing.description',
        dependsOn: ['contacts'],
        apiPrefixes: ['/api/billing'],
        publicPrefixes: ['/e', '/i'],
        navPaths: ['/facturacion'],
    },
    {
        key: 'messaging',
        labelKey: 'platformAdmin.modules.messaging.label',
        descriptionKey: 'platformAdmin.modules.messaging.description',
        dependsOn: ['contacts'],
        apiPrefixes: ['/api/dm-triage', '/api/dm', '/api/settings/whatsapp'],
        publicPrefixes: [],
        navPaths: ['/mensajes', '/mensajes/triage'],
    },
];
const MODULE_BY_KEY = new Map(ACCOUNT_MODULES.map(m => [m.key, m]));
export function getAccountModuleDef(key) {
    const def = MODULE_BY_KEY.get(key);
    if (!def)
        throw new Error(`Módulo desconocido: ${key}`);
    return def;
}
/**
 * Módulos habilitados que dependen de `key`. Vacío = se puede apagar.
 *
 * Decisión de Eduardo (2026-08-17): el panel NO deja apagar contactos con
 * dependientes prendidos y nombra cuáles — no se deja romper la cuenta de un
 * cliente para explicarlo después. Misma función la usan el frontend (para
 * deshabilitar el interruptor con el motivo a la vista) y el backend (para
 * rechazar el PUT), así el chequeo es uno solo y no dos que pueden divergir.
 */
export function blockingDependents(key, modules) {
    return ACCOUNT_MODULES
        .filter(m => modules[m.key] && m.dependsOn.includes(key))
        .map(m => m.key);
}
/** Dependencias faltantes al PRENDER `key` — el espejo de blockingDependents. */
export function missingDependencies(key, modules) {
    return getAccountModuleDef(key).dependsOn.filter(dep => !modules[dep]);
}
// ── Estado administrativo de la cuenta ───────────────────────────────────────
// 'suspended' y 'closed' son cosas distintas y se ven separadas en la lista
// (decisión de Eduardo): suspended = "no pagó, puede volver mañana"; closed =
// "esta relación terminó". Los dos bloquean login y sacan a la cuenta del loop
// del worker; ninguno de los dos borra datos ni archivos.
export const ACCOUNT_STATUSES = ['provisioning', 'active', 'suspended', 'closed'];
/** Estados que dejan entrar: solo 'active'. Todo lo demás es 401 en resolveAccount. */
export function isAccountUsable(status) {
    return status === 'active';
}
// ── Período de prueba (2026-08-18) ───────────────────────────────────────────
/** Vigente = hay fecha de fin y todavía no pasó. Compartido entre server y web
 *  para que las dos partes decidan igual — un cliente que ve "te quedan 3 días"
 *  y un backend que ya lo cortó sería peor que cualquiera de los dos solo. */
export function isTrialActive(trialEndsAt, now = new Date()) {
    if (!trialEndsAt)
        return false;
    return new Date(trialEndsAt).getTime() > now.getTime();
}
/** Días enteros que faltan. 0 = vence hoy. Negativo = ya venció. */
export function trialDaysLeft(trialEndsAt, now = new Date()) {
    if (!trialEndsAt)
        return null;
    return Math.ceil((new Date(trialEndsAt).getTime() - now.getTime()) / 86_400_000);
}
/** A cuántos días del vencimiento se avisa por email. */
export const TRIAL_WARNING_DAYS = 7;
/**
 * Los días de demo que recibe una cuenta nueva.
 *
 * Es el valor por defecto, no un límite: desde el panel de plataforma se
 * concede la cantidad que se quiera (PATCH /accounts/:id/trial), y ese es el
 * único camino para volver a dar una demo. Nadie se auto-renueva la suya
 * volviendo a pasar por la inducción.
 */
export const TRIAL_DAYS_DEFAULT = 15;
//# sourceMappingURL=account-modules.js.map